import type { LoadRequest } from '@foundation-player/api'
import type { VideoProps, VideoTags } from '@/types/videoTypes'
import type {
  AdSlot,
  Environment,
} from 'node_modules/@foundation-player/api/dist/public/generated/load-request.schema'
import { FALLBACK_IMAGE_URL } from '../Image/FallbackImage'
import { getVideoDuration } from '@/utils/videoPlayer'

export const defaultErrorMessage = 'Dieses Video ist leider nicht verfügbar.'
export const geoBlockingErrorMessage =
  'Dieses Video ist in deinem Land leider nicht verfügbar.'
export const dateErrorMessage = 'Dieses Video ist leider nicht mehr verfügbar.'

export type SlotType = 'pre' | 'mid' | 'post'

export const getVpShare = (pathOrIvw: string): string => {
  const raw =
    pathOrIvw && pathOrIvw.includes('/')
      ? pathOrIvw
      : typeof window !== 'undefined'
        ? window.location.pathname
        : ''

  let parts = raw.split('/').filter(Boolean)
  if (parts.length > 3) parts = parts.slice(0, 3)
  const suffix = parts.length ? `/${parts.join('_')}` : ''
  return `rtl_wetter${suffix}`
}

const getSeoTitle = (primary?: string, secondary?: string): string => {
  if (!primary && !secondary) return ''
  if (!secondary && primary) return primary
  return secondary + (primary ? ': ' + primary : '')
}

export const foundationEnv: Environment = (() => {
  const fromVar = (process.env.NEXT_PUBLIC_FOUNDATION_ENV || '').toLowerCase()
  if (fromVar === 'dev' || fromVar === 'preprod' || fromVar === 'prod') {
    return fromVar as Environment
  }
  return 'dev'
})()

const createAdCall = (
  id: string,
  conlen: number,
  slot: SlotType,
  tcString: string,
  ivw: string,
  moltenVideoTracking: string,
): string => {
  const params = new URLSearchParams({
    type: 'vast4',
    fwd_adb: '0',
    s: getVpShare(
      ivw || (typeof window !== 'undefined' ? window.location.pathname : ''),
    ),
    sz: slot,
    tagver: '6.1.1',
    fwd_comp: '0',
    ang_cntp: 'none',
    ang_streamid: crypto.getRandomValues(new Uint32Array(1)).toString(),
    ang_conlen: Math.round(conlen).toString(),
    fwd_conid: id,
    rnd: crypto.getRandomValues(new Uint32Array(1)).toString(),
    ang_ref: encodeURI(window?.location?.href ?? 'https://www.wetter.de'),
    plcmt: '1',
  }).toString()

  return `https://ad-ipd.sxp.smartclip.net/select?${params}&${moltenVideoTracking}&consent=${tcString}`
}

export const getStartType = (
  autoplay: boolean,
  currentVideoIndex: number,
): number => {
  return autoplay && currentVideoIndex > 0 ? 1 : 0
}

interface LoadRequestProps {
  video: VideoProps
  tcString: string
  ivwCode: string
  moltenVideoTracking: string
  postRoll?: boolean
  currentVideoIndex?: number
  poster?: string
  autoplay?: boolean
  fast?: boolean
  ads?: boolean
}

export const createLoadRequest = ({
  video,
  tcString,
  ivwCode,
  moltenVideoTracking,
  currentVideoIndex,
  postRoll = true,
  poster = FALLBACK_IMAGE_URL,
  autoplay = false,
  fast = false,
  ads = false,
}: LoadRequestProps): LoadRequest => {
  const { fields, sourceId, tags } = video
  const videoDuration = getVideoDuration(fields.encoding)
  const title = decodeURIComponent(fields.title)
  const videoTags = tags?.map((item: VideoTags) => item.name) ?? []

  if (fields?.contributor) videoTags.push(fields.contributor)

  const slots: Array<AdSlot> = []

  const preAdCall = ads
    ? createAdCall(
        sourceId,
        videoDuration,
        'pre',
        tcString,
        ivwCode,
        moltenVideoTracking,
      )
    : undefined
  if (preAdCall)
    slots.push({ adCall: preAdCall, offsetInSeconds: 0, type: 'linear' })

  // Only mid-roll on long videos AND ensure offset < duration
  const midAdCall =
    ads && videoDuration > 480
      ? createAdCall(
          sourceId,
          videoDuration,
          'mid',
          tcString,
          ivwCode,
          moltenVideoTracking,
        )
      : undefined
  if (midAdCall && videoDuration > 180) {
    slots.push({ adCall: midAdCall, offsetInSeconds: 180, type: 'linear' })
  }

  const postAdCall =
    ads && postRoll && videoDuration > 30
      ? createAdCall(
          sourceId,
          videoDuration,
          'post',
          tcString,
          ivwCode,
          moltenVideoTracking,
        )
      : undefined
  if (postAdCall)
    slots.push({ adCall: postAdCall, offsetInSeconds: -1, type: 'linear' })

  return {
    contentType: fast ? 'fast' : 'vod',
    contentId: sourceId,
    autoplay,
    remoteConfig: {
      environment: foundationEnv,
      clientIdentifier: 'html5/rtldigitalnews/wetter',
    },
    contentMetadata: {
      title,
      headline: title,
      secondaryHeadline: decodeURIComponent(fields?.subtitle ?? ''),
      durationInSeconds: videoDuration,
      posterUrl: poster,
      firstPublicationDate: video.createdAt ?? '',
      lastPublicationDate: video.lastmodifiedAt ?? '',
    },
    plugins: {
      'foundation.plugin.smartclip': {
        enabled: true,
        adSlots: slots,
        smartclipConfig: {
          adReinsertion: {
            homad: {
              enabled: true,
              setup: {
                clientConfig:
                  'https://cdn.static-fra.de/now/sc/wetter.de.clientConfig.json',
              },
            },
          },
        },
      },
      'foundation.plugin.heartbeat': {
        enabled: true,
        startType: getStartType(autoplay, currentVideoIndex ?? 0),
      },
      'foundation.plugin.googleAnalytics4': {
        enabled: true,
        firstHeadline: getSeoTitle(
          fields['first-title'],
          fields['first-subtitle'],
        ),
        headline: getSeoTitle(fields.title, fields.subtitle),
        tags: videoTags,
        firstPublicationDate: video.publishedAt ?? undefined,
        lastPublicationDate: video.lastmodifiedAt ?? undefined,
        contentType: 'video',
        editorialUnit: fields.producer ?? '',
      },
    },
  }
}
